import React from 'react'
import { Button, Card, Form, Input, Spin, Alert, Checkbox, Modal, Row, Col } from 'antd'
import InputMask from 'react-input-mask'
import CoverageArea from '@reggora-app/coverage-map'
import TaxInformation from '../LegalVerification/TaxInformation'
import PropertyDataCollectorTermsAndConditions from './PropertyDataCollectorTermsAndConditions'
import PasswordRequirements from '@reggora_shared_ui/components/PasswordRequirements'
import PropertyDataCollectorCredentials from './PropertyDataCollectorCredentials'
import StateAutoComplete from '@components/Shared/StateAutoComplete'
import ZipInput, { defaultZipRules, createZipValidationRules } from '@components/Shared/ZipInput'
import { createStateValidator } from '@helpers/stateValidation'
import './PropertyDataCollectorSetup.scss'

const labelCol = { span: 24 }

const PropertyDataCollectorSetup = ({
  // Form props
  formRef,
  coverageAreaRef,
  submitting,
  validationErrors = [],
  agreed,
  termsModalVisible,
  formValues,
  formInitialValues,

  // Validation rules
  passwordRules,
  confirmPasswordRules,
  passwordRequirementsRules,
  termsRules,

  // Coverage props
  coverageProps,

  // Credentials props
  credentials,
  onCredentialsChange,

  // Starting location props
  startingLocationDifferent,
  onStartingLocationChange,

  // Handlers
  onSubmit,
  onFormValuesChange,
  onPasswordRequirementsChange,
  onTermsAgreed,
  onAgree,
  onDisagree,
  onOpenTermsModal,
  onClearValidationErrors,

  // Shell user props
  shellUser,
}) => {
  // Error banner/alert component
  const renderErrorSummary = () => {
    if (!validationErrors || validationErrors.length === 0) {
      return null
    }

    return (
      <Alert
        message={`Please correct the following ${validationErrors.length > 1 ? 'errors' : 'error'}:`}
        description={
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {validationErrors.map((error, index) => (
              <li key={index} style={{ marginBottom: '4px' }}>
                {error.message}
              </li>
            ))}
          </ul>
        }
        type="error"
        showIcon
        style={{ marginBottom: '20px' }}
        closable
        onClose={onClearValidationErrors}
      />
    )
  }

  return (
    <div className="modern-signup-form">
      <Form
        ref={formRef}
        initialValues={formInitialValues}
        validateTrigger="onSubmit"
        onValuesChange={onFormValuesChange}
        className="property-data-collector-setup"
        layout="vertical"
      >
        {/* Error banner/alert displayed at top of form */}
        {renderErrorSummary()}

        {/* Basic Information & Password */}
        <Card className="basic-info-password-card">
          <div className="card-content-grid">
            <div className="basic-info-column">
              <h3>Basic Information</h3>
              <Form.Item
                label="First Name"
                labelCol={labelCol}
                name="firstname"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your first name',
                  },
                ]}
              >
                <Input placeholder="First Name" />
              </Form.Item>
              <Form.Item
                label="Last Name"
                labelCol={labelCol}
                name="lastname"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your last name',
                  },
                ]}
              >
                <Input placeholder="Last Name" />
              </Form.Item>
              <Form.Item
                label="Email"
                labelCol={labelCol}
                name="email"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your email',
                    type: 'email',
                  },
                ]}
              >
                <Input type="email" disabled={true} placeholder="Email" />
              </Form.Item>
              <Form.Item
                label="Cell Phone Number"
                labelCol={labelCol}
                name="cell_phone"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your cell phone number',
                  },
                ]}
              >
                <InputMask mask="(999) 999-9999" maskChar=" " type="tel" />
              </Form.Item>
            </div>

            <div className="password-column">
              <h3>Password</h3>
              <Form.Item label="Password" labelCol={labelCol} name="password" rules={passwordRules}>
                <Input type="password" />
              </Form.Item>
              <Form.Item
                label="Confirm Password"
                labelCol={labelCol}
                name="confirm_password"
                rules={confirmPasswordRules}
              >
                <Input type="password" />
              </Form.Item>
              <Form.Item name="password_requirements" rules={passwordRequirementsRules}>
                <PasswordRequirements
                  password={formValues.password}
                  confirmPassword={formValues.confirm_password}
                  onRequirementsChange={onPasswordRequirementsChange}
                />
              </Form.Item>
            </div>
          </div>
        </Card>

        {/* Address Information */}
        <Card>
          <h3>Address Information</h3>

          <Form.Item
            label="Street Address"
            labelCol={labelCol}
            name="primaryStreetAddress"
            rules={[
              {
                required: true,
                message: 'Please enter your street address',
              },
            ]}
          >
            <Input placeholder="123 Main Street" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="City"
                labelCol={labelCol}
                name="primaryCity"
                rules={[
                  {
                    required: true,
                    message: 'Please enter your city',
                  },
                ]}
              >
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="State"
                labelCol={labelCol}
                name="primaryState"
                rules={[
                  {
                    required: true,
                    message: 'Please select your state',
                  },
                  createStateValidator('Please select a valid state from the list'),
                ]}
              >
                <StateAutoComplete placeholder="State" size="large" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="ZIP Code" labelCol={labelCol} name="primaryZipCode" rules={defaultZipRules}>
                <ZipInput size="large" />
              </Form.Item>
            </Col>
          </Row>

          <div className="starting-location-checkbox">
            <Checkbox checked={startingLocationDifferent} onChange={(e) => onStartingLocationChange(e.target.checked)}>
              My workday starts somewhere else
            </Checkbox>
          </div>

          {startingLocationDifferent && (
            <div className="starting-location-field">
              <div className="label-with-description" style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#1890ff' }}>Starting Location</span>
                <span className="label-description">
                  This is the location where you often begin your day. We’ll use it to send you orders that are close
                  by, so you can get to them faster.
                </span>
              </div>

              <Form.Item
                label="Street Address"
                labelCol={labelCol}
                name="startingStreetAddress"
                rules={[
                  {
                    required: startingLocationDifferent,
                    message: 'Please enter your starting location street address',
                  },
                ]}
              >
                <Input placeholder="123 Starting Street" size="large" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    labelCol={labelCol}
                    name="startingCity"
                    rules={[
                      {
                        required: startingLocationDifferent,
                        message: 'Please enter your starting location city',
                      },
                    ]}
                  >
                    <Input placeholder="City" size="large" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="State"
                    labelCol={labelCol}
                    name="startingState"
                    rules={[
                      {
                        required: startingLocationDifferent,
                        message: 'Please select your starting location state',
                      },
                      createStateValidator('Please select a valid state from the list'),
                    ]}
                  >
                    <StateAutoComplete placeholder="State" size="large" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="ZIP Code"
                    labelCol={labelCol}
                    name="startingZipCode"
                    rules={createZipValidationRules({
                      required: startingLocationDifferent,
                      requiredMessage: 'Please enter your starting location ZIP code',
                    })}
                  >
                    <ZipInput size="large" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </Card>

        {/* Coverage Area */}
        {coverageProps && (
          <div className="coverage-area-wrapper" ref={coverageAreaRef}>
            <CoverageArea
              {...coverageProps}
              showError={coverageProps.showError && !validationErrors.find((error) => error.field === 'coverage')}
            />
          </div>
        )}

        {/* Credentials Section */}
        <Card>
          <h3>Licenses and Certifications</h3>
          <PropertyDataCollectorCredentials
            initialCredentials={credentials}
            onCredentialsChange={onCredentialsChange}
          />
        </Card>

        {/* Tax Information */}
        <TaxInformation
          loading={false}
          subHeadingText="Please provide your tax information so we can pay you for completed orders. All of your information is kept secure and confidential."
          shellUser={shellUser}
        />

        {/* Terms and Conditions */}
        <Card>
          <h3>Terms and Conditions</h3>
          <Form.Item className="required-checkbox" name="terms_and_conditions" rules={termsRules}>
            <Checkbox
              name="terms_and_conditions"
              onChange={(e) => onTermsAgreed(e.target.checked)}
              checked={agreed}
              value={true}
            >
              <span>I agree to the&nbsp;</span>
              <Button type="link" onClick={onOpenTermsModal} style={{ paddingLeft: 0 }}>
                Terms and Conditions
              </Button>
            </Checkbox>
          </Form.Item>
        </Card>

        {/* Submit Button */}
        <Card className="controls">
          <Button className="submit-button" type="primary" disabled={submitting} onClick={onSubmit}>
            {submitting ? <Spin /> : 'Create Account'}
          </Button>
        </Card>

        {/* Terms and Conditions Modal */}
        <Modal
          className="terms-modal"
          title="Terms and Conditions"
          onOk={onAgree}
          onCancel={onDisagree}
          visible={termsModalVisible}
          footer={[
            <Button key="disagree" onClick={onDisagree}>
              Disagree
            </Button>,
            <Button key="agree" type="primary" onClick={onAgree}>
              Agree
            </Button>,
          ]}
        >
          <PropertyDataCollectorTermsAndConditions />
        </Modal>
      </Form>
    </div>
  )
}

export default PropertyDataCollectorSetup
